/**
 * Mandate Dashboard — command center for the transition.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Compass } from 'lucide-react';
import { useMandateStore } from '../../stores/mandate/mandateStore';
import { MandateNoTransition } from '../../components/mandate/MandateNoTransition';
import { PageHeader } from '../../components/prioritization/PageHeader';
import { getDayNumber, getDaysRemaining } from '../../types/mandate';
import type { MandatePriority } from '../../types/mandate';

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays > 0) return `${diffDays}d ago`;
  if (diffHours > 0) return `${diffHours}h ago`;
  if (diffMins > 0) return `${diffMins}m ago`;
  return 'just now';
}

export default function MandateDashboard() {
  const {
    transition,
    outcomeVersions,
    priorities,
    decisions,
    reranks,
    currentOutcome,
    fetchAll,
    rerankPriority,
  } = useMandateStore();

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Check-in state — the "Day Two" return flow
  const [checkingIn, setCheckingIn] = useState(false);
  const [rerankingId, setRerankingId] = useState<string | null>(null);
  const [rerankTarget, setRerankTarget] = useState(1);
  const [rerankSignal, setRerankSignal] = useState('');
  const [reranking, setReranking] = useState(false);

  // Onboarding state
  if (!transition) {
    return <MandateNoTransition />;
  }

  const dayNumber = transition.startDate ? getDayNumber(transition.startDate) : null;
  const daysRemaining = transition.endDate ? getDaysRemaining(transition.endDate) : null;
  const currentVersion = currentOutcome?.version ?? 1;

  const statusConfig = {
    setup: { label: 'Setting up', cls: 'bg-[var(--mustard-dim)] text-[var(--mustard)]' },
    active: { label: 'Active', cls: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' },
    complete: { label: 'Complete', cls: 'bg-[var(--slate-dim)] text-[var(--text-sec)]' },
    archived: { label: 'Archived', cls: 'bg-[var(--hover-tint)] text-[var(--text-sec)]' },
  };
  const statusInfo = statusConfig[transition.status] ?? statusConfig.setup;

  const activePriorities = priorities.filter((p) => p.status === 'active');
  const top3 = activePriorities.slice(0, 3);

  // Most recent re-rank date
  const lastRerankDate =
    reranks.length > 0
      ? reranks.reduce((a, b) => (new Date(a.occurredAt) > new Date(b.occurredAt) ? a : b)).occurredAt
      : null;

  function openRerank(p: MandatePriority) {
    setRerankingId(p.id);
    setRerankTarget(p.rank);
    setRerankSignal('');
  }

  async function handleRerank(p: MandatePriority) {
    if (!rerankSignal.trim()) return;
    setReranking(true);
    try {
      await rerankPriority(p.id, rerankTarget, rerankSignal.trim());
      setRerankingId(null);
    } finally {
      setReranking(false);
    }
  }

  // Recent activity: merge decisions + reranks, sort desc
  type ActivityItem = { date: string; label: string };
  const activityItems: ActivityItem[] = [
    ...decisions.map((d) => ({ date: d.createdAt, label: `Decision: ${d.title}` })),
    ...reranks.map((r) => {
      const p = priorities.find((pr) => pr.id === r.priorityId);
      return {
        date: r.occurredAt,
        label: `Re-ranked "${p?.title ?? 'priority'}" #${r.previousRank ?? '?'} → #${r.newRank ?? '?'}`,
      };
    }),
  ]
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, 3);

  return (
    <div>
      <PageHeader
        icon={<Compass className="h-4 w-4 text-[var(--text-ter)]" />}
        iconBg="bg-[var(--slate-dim)]"
        title={`${transition.role} @ ${transition.organization}`}
        subtitle={transition.title}
        actions={
          <span className={`text-xs font-medium px-2 py-1 rounded-full ${statusInfo.cls}`}>
            {statusInfo.label}
          </span>
        }
      />

      {/* Day counter */}
      <div className="flex flex-wrap items-end gap-x-3 gap-y-1 mb-8">
        {dayNumber !== null ? (
          <>
            <span className="text-4xl font-bold text-[var(--text-pri)]">Day {dayNumber}</span>
            <span className="text-lg text-[var(--text-ter)] mb-0.5">of 100</span>
            {daysRemaining !== null && (
              <span className="text-sm text-[var(--text-ter)] mb-1 ml-2">
                {daysRemaining} days remaining
              </span>
            )}
          </>
        ) : (
          <span className="text-[var(--text-ter)]">Transition not yet started</span>
        )}
      </div>

      {/* Three-column cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {/* Day 100 Outcome */}
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-[var(--text-sec)]">Day 100 Outcome</h3>
            {currentVersion > 1 && (
              <span className="text-xs bg-[var(--slate-dim)] text-[var(--text-sec)] font-medium px-1.5 py-0.5 rounded">
                Version {currentVersion}
              </span>
            )}
          </div>
          {currentOutcome ? (
            <p className="text-sm text-[var(--text-sec)] line-clamp-3 mb-3 leading-relaxed">
              {currentOutcome.hypothesis}
            </p>
          ) : (
            <p className="text-sm text-[var(--text-ter)] mb-3">No hypothesis set yet.</p>
          )}
          <Link
            to="/apps/mandate/outcome"
            className="text-sm text-[var(--text-ter)] hover:text-[var(--text-ter)] font-medium"
          >
            Edit →
          </Link>
        </div>

        {/* Priorities Stack */}
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-[var(--text-sec)]">Priorities Stack</h3>
            <span className="text-xs text-[var(--text-ter)]">{activePriorities.length} total</span>
          </div>
          {top3.length > 0 ? (
            <ul className="space-y-1.5 mb-3">
              {top3.map((p) => (
                <li key={p.id} className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-primary-100 text-primary-700 text-xs font-bold flex items-center justify-center flex-shrink-0">
                    {p.rank}
                  </span>
                  <span className="text-sm text-[var(--text-sec)] truncate">{p.title}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-[var(--text-ter)] mb-3">No priorities yet.</p>
          )}
          <Link
            to="/apps/mandate/priorities"
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            View all →
          </Link>
        </div>

        {/* Decision Record */}
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-[var(--text-sec)]">Decision Record</h3>
            <span className="text-xs text-[var(--text-ter)]">{decisions.length} decisions</span>
          </div>
          {decisions.length > 0 ? (
            <ul className="space-y-1 mb-3">
              {decisions.slice(0, 2).map((d) => (
                <li key={d.id} className="text-sm text-[var(--text-sec)] truncate">
                  {d.title}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-[var(--text-ter)] mb-3">No decisions logged yet.</p>
          )}
          <Link
            to="/apps/mandate/decisions"
            className="text-sm text-[var(--mustard)] hover:text-[var(--mustard)] font-medium"
          >
            View all →
          </Link>
        </div>
      </div>

      {/* Check-in panel — the "Day Two" return flow */}
      {activePriorities.length > 0 && (
        <div className="mb-8">
          {!checkingIn ? (
            <div className="bg-[var(--surface)] border border-[var(--rule)] p-5 flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-[var(--text-pri)]">Something has changed.</p>
                <p className="text-sm text-[var(--text-ter)] mt-0.5">
                  {lastRerankDate
                    ? `Your stack was last updated ${formatRelativeTime(lastRerankDate)}.`
                    : "Your stack hasn't been re-evaluated since Day 1."}{' '}
                  Name what moved and why.
                </p>
              </div>
              <button
                onClick={() => setCheckingIn(true)}
                className="bg-primary-600 hover:bg-primary-700 text-white font-medium px-4 py-2 text-sm flex-shrink-0 ml-4"
              >
                Check in →
              </button>
            </div>
          ) : (
            <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-sm font-semibold text-[var(--text-pri)]">
                    Day {dayNumber} check-in
                  </p>
                  <p className="text-xs text-[var(--text-ter)] mt-0.5">
                    Select a priority to re-rank. Every change needs a signal.
                  </p>
                </div>
                <button
                  onClick={() => {
                    setCheckingIn(false);
                    setRerankingId(null);
                  }}
                  className="text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)]"
                >
                  ✕ Close
                </button>
              </div>

              <div className="space-y-2">
                {activePriorities.map((p) => (
                  <div key={p.id}>
                    <div
                      className={`flex items-center gap-3 p-3  border cursor-pointer transition-all ${
                        rerankingId === p.id
                          ? 'border-primary-300 bg-primary-50'
                          : 'border-[var(--rule)] hover:border-[var(--rule)] hover:bg-[var(--surface)]'
                      }`}
                      onClick={() => (rerankingId === p.id ? setRerankingId(null) : openRerank(p))}
                    >
                      <span className="w-6 h-6 rounded-full bg-primary-100 text-primary-700 text-xs font-bold flex items-center justify-center flex-shrink-0">
                        {p.rank}
                      </span>
                      <span className="text-sm font-medium text-[var(--text-pri)] flex-1">{p.title}</span>
                      <span className="text-xs text-[var(--text-ter)]">
                        {rerankingId === p.id ? 'Cancel' : 'Re-rank'}
                      </span>
                    </div>

                    {rerankingId === p.id && (
                      <div className="mt-2 ml-9 space-y-2">
                        <div className="flex items-center gap-3">
                          <div>
                            <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">
                              Move to rank:
                            </label>
                            <input
                              type="number"
                              min={1}
                              max={activePriorities.length}
                              value={rerankTarget}
                              onChange={(e) => setRerankTarget(Number(e.target.value))}
                              className="w-16 border border-[var(--rule)] rounded px-2 py-1.5 text-sm text-[var(--text-pri)] focus:outline-none focus:ring-2 focus:ring-primary-500"
                            />
                          </div>
                          <div className="flex-1">
                            <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">
                              What changed? <span className="text-[var(--coral)]">*</span>
                            </label>
                            <textarea
                              value={rerankSignal}
                              onChange={(e) => setRerankSignal(e.target.value)}
                              rows={2}
                              autoFocus
                              className="w-full border border-[var(--rule)] rounded px-2 py-1.5 text-sm text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                              placeholder="What new information or event drove this re-rank?"
                            />
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleRerank(p)}
                            disabled={!rerankSignal.trim() || reranking}
                            className="bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white font-medium px-3 py-1.5 rounded text-xs"
                          >
                            {reranking ? 'Saving...' : 'Record signal'}
                          </button>
                          <button
                            onClick={() => setRerankingId(null)}
                            className="text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)]"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-4 pt-3 border-t border-[var(--rule)]">
                <Link
                  to="/apps/mandate/priorities"
                  className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                >
                  Go to full Priorities Stack →
                </Link>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Recent activity */}
      <div>
        <h2 className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wider mb-3">
          Recent activity
        </h2>
        {activityItems.length > 0 ? (
          <ul className="space-y-2">
            {activityItems.map((item, i) => (
              <li key={i} className="flex items-center justify-between">
                <span className="text-sm text-[var(--text-sec)]">{item.label}</span>
                <span className="text-xs text-[var(--text-ter)]">{formatRelativeTime(item.date)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-[var(--text-ter)]">No activity yet. Start by setting your priorities.</p>
        )}
      </div>

      {/* Version history count */}
      {outcomeVersions.length > 1 && (
        <div className="mt-4 text-xs text-[var(--text-ter)]">
          Outcome revised {outcomeVersions.length - 1} time{outcomeVersions.length > 2 ? 's' : ''} so far.
        </div>
      )}

      {/* Record link */}
      <div className="mt-6 pt-6 border-t border-[var(--rule)]">
        <Link
          to="/apps/mandate/record"
          className="text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)] font-medium"
        >
          View the full transition record →
        </Link>
      </div>
    </div>
  );
}
